import type * as PIXI from "pixi.js"
import Model from "../model/model"
import { type ID, type Idea } from "../model/types"
import DragPreviewRenderer, { type DragPreviewOffset } from "./drag_preview_renderer"
import FullTitleRenderer from "./full_title_renderer"
import IdeaGraphicsRenderer from "./idea_graphics_renderer"
import IdeaTextRenderer from "./idea_text_renderer"
import InteractionOverlayRenderer, {
  type LassoBox,
  type OpenCellOverlay,
  type PlaneRectangle,
} from "./interaction_overlay_renderer"
import { create_pixi_scene, type PixiScene, type PixiSceneOptions } from "./pixi_scene"
import { renderBackgroundToGraphic, renderHomeDotToGraphic } from "./renderers"
import SelectionRenderer from "./selection_renderer"
import ViewportRenderer, {
  type ScaleViewport,
  type ViewBounds,
  type ViewportSize,
} from "./viewport_renderer"
import { type CameraState, type ViewLocation } from "./coords"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"

type StagePointerEventName =
  | "pointerdown"
  | "pointermove"
  | "pointerup"
  | "pointerover"
  | "pointerout"
type StageEventName = StagePointerEventName | "wheel"
type StageEvent<T extends StageEventName> = T extends "wheel"
  ? PIXI.FederatedWheelEvent
  : PIXI.FederatedPointerEvent

export default class PixiRenderer {
  private scene: PixiScene
  drag_preview_renderer: DragPreviewRenderer
  full_title_renderer: FullTitleRenderer
  idea_graphics_renderer: IdeaGraphicsRenderer
  idea_text_renderer: IdeaTextRenderer
  interaction_overlay_renderer: InteractionOverlayRenderer
  selection_renderer: SelectionRenderer
  viewport_renderer: ViewportRenderer
  private idea_colour_resolver: IdeaColourResolver = defaultIdeaColourResolver

  constructor(options: PixiSceneOptions) {
    this.scene = create_pixi_scene(options)
    const idea_colour_resolver = () => this.idea_colour_resolver
    this.drag_preview_renderer = new DragPreviewRenderer(this.scene, idea_colour_resolver)
    this.full_title_renderer = new FullTitleRenderer(
      this.scene.app,
      this.scene.layers,
      idea_colour_resolver,
    )
    this.idea_graphics_renderer = new IdeaGraphicsRenderer(
      this.scene.app,
      this.scene.layers,
      idea_colour_resolver,
    )
    this.idea_text_renderer = new IdeaTextRenderer(
      () => this.scene.layers.ideas,
      idea_colour_resolver,
      this.scene.request_render,
    )
    this.interaction_overlay_renderer = new InteractionOverlayRenderer(this.scene)
    this.selection_renderer = new SelectionRenderer(this.scene.layers.selection)
    this.viewport_renderer = new ViewportRenderer(this.scene)
  }

  render_fresh_ideas(model: Model, hiding_ideas: Idea[] = []): PIXI.Graphics {
    const ideas_graphic = this.idea_graphics_renderer.render_fresh_ideas(model, hiding_ideas)
    this.scene.request_render()
    return ideas_graphic
  }

  set_idea_colour_resolver(idea_colour_resolver: IdeaColourResolver): void {
    this.idea_colour_resolver = idea_colour_resolver
  }

  set_dark_mode(is_dark_mode: boolean): void {
    this.scene.is_dark_mode = is_dark_mode
    renderBackgroundToGraphic(this.scene.layers.background, is_dark_mode, this.screen_width())
    renderHomeDotToGraphic(this.scene.layers.home, is_dark_mode)
    this.reposition_grid(this.camera_state(this.scene.app.stage.scale.x || 1))
  }

  render_idea_text(idea: Idea): void {
    this.idea_text_renderer.render_idea_text(idea)
    this.scene.request_render()
  }

  clear_idea_text(idea: Idea): void {
    this.idea_text_renderer.clear_idea_text(idea)
    this.scene.request_render()
  }

  rerender_idea_text(idea: Idea): void {
    this.idea_text_renderer.rerender_idea_text(idea)
    this.scene.request_render()
  }

  rerender_rendered_idea_texts(model: Model): void {
    this.idea_text_renderer.rerender_rendered_idea_texts(model.store.ideas)
    this.scene.request_render()
  }

  hide_idea_texts(idea_ids: ID[]): void {
    this.idea_text_renderer.hide_idea_texts(idea_ids)
    this.scene.request_render()
  }

  show_idea_texts(idea_ids: ID[]): void {
    this.idea_text_renderer.show_idea_texts(idea_ids)
    this.scene.request_render()
  }

  set_compact_idea_texts_visible(is_visible: boolean): void {
    this.idea_text_renderer.set_texts_visible(is_visible)
    this.scene.request_render()
  }

  render_full_titles(ideas: Idea[]): void {
    this.full_title_renderer.render_titles(ideas)
    this.scene.request_render()
  }

  clear_full_titles(): void {
    this.full_title_renderer.clear_titles()
    this.scene.request_render()
  }

  hide_full_titles(idea_ids: ID[]): void {
    this.full_title_renderer.hide_titles(idea_ids)
    this.scene.request_render()
  }

  position_idea_text(idea: Idea): void {
    this.idea_text_renderer.position_idea_text(idea)
    this.scene.request_render()
  }

  render_onscreen_idea_texts(ideas: Idea[], is_idea_onscreen: (idea: Idea) => boolean): void {
    this.idea_text_renderer.render_onscreen_idea_texts(ideas, is_idea_onscreen)
    this.scene.request_render()
  }

  lazy_render_idea_texts(ideas: Idea[]): void {
    this.idea_text_renderer.lazy_render_idea_texts(ideas)
  }

  render_selection(
    selected_ideas: Idea[],
    re_render: boolean,
    plane_offset: { dpx: number; dpy: number } = { dpx: 0, dpy: 0 },
  ): void {
    this.selection_renderer.render_selection(selected_ideas, re_render, plane_offset)
    this.scene.request_render()
  }

  set_hover_visible(is_visible: boolean): void {
    this.interaction_overlay_renderer.set_hover_visible(is_visible)
    this.scene.request_render()
  }

  hide_pointer_square(): void {
    if (this.interaction_overlay_renderer.hide_pointer_square()) this.scene.request_render()
  }

  show_pointer_square(rectangle: PlaneRectangle): void {
    if (this.interaction_overlay_renderer.show_pointer_square(rectangle))
      this.scene.request_render()
  }

  render_open_cell_overlay(open_cell_overlay: OpenCellOverlay): void {
    this.interaction_overlay_renderer.render_open_cell_overlay(open_cell_overlay)
    this.scene.request_render()
  }

  hide_open_cell_overlay(): void {
    this.interaction_overlay_renderer.hide_open_cell_overlay()
    this.scene.request_render()
  }

  show_lasso(lasso_box: LassoBox): void {
    this.interaction_overlay_renderer.show_lasso(lasso_box)
    this.scene.request_render()
  }

  hide_lasso(): void {
    this.interaction_overlay_renderer.hide_lasso()
    this.scene.request_render()
  }

  set_ideas_cursor(cursor: string): void {
    this.drag_preview_renderer.set_ideas_cursor(cursor)
  }

  render_drag_preview(ideas: Idea[], scale: number): void {
    this.drag_preview_renderer.render_drag_preview(ideas, scale)
    this.scene.request_render()
  }

  move_drag_preview(plane_offset: DragPreviewOffset): void {
    this.drag_preview_renderer.move_drag_preview(plane_offset)
    this.scene.request_render()
  }

  clear_drag_preview(): void {
    this.drag_preview_renderer.clear_drag_preview()
    this.scene.request_render()
  }

  on_stage_event<T extends StageEventName>(
    event_name: T,
    handler: (event: StageEvent<T>) => void,
  ): void {
    this.scene.app.stage.on(event_name, handler as (...args: unknown[]) => void)
  }

  stage_position(): { x: number; y: number } {
    return this.scene.app.stage.position
  }

  set_stage_cursor(cursor: string): void {
    this.scene.app.stage.cursor = cursor
  }

  has_idea_graphics(): boolean {
    return this.scene.layers.ideas != null
  }

  canvas(): HTMLCanvasElement {
    return this.viewport_renderer.canvas()
  }

  set_resize_target(dom_element: HTMLElement): void {
    this.viewport_renderer.set_resize_target(dom_element)
  }

  screen_width(): number {
    return this.viewport_renderer.screen_width()
  }

  screen_height(): number {
    return this.viewport_renderer.screen_height()
  }

  camera_state(scale: number): CameraState {
    return this.viewport_renderer.camera_state(scale)
  }

  view_bounds(scale: number): ViewBounds {
    return this.viewport_renderer.view_bounds(scale)
  }

  apply_view_bounds(view_bounds: ViewBounds): void {
    this.viewport_renderer.apply_view_bounds(view_bounds)
    this.scene.request_render()
  }

  reset_view(viewport_size: ViewportSize): void {
    this.viewport_renderer.reset_view(viewport_size)
    this.scene.request_render()
  }

  move_view(view_location: ViewLocation): void {
    this.viewport_renderer.move_view(view_location)
    this.scene.request_render()
  }

  scale_view(scale_viewport: ScaleViewport): void {
    this.viewport_renderer.scale_view(scale_viewport)
    this.scene.request_render()
  }

  reposition_grid(camera_state: CameraState): void {
    this.viewport_renderer.reposition_grid(camera_state)
    this.scene.request_render()
  }

  destroy(): void {
    this.scene.cancel_render()
    this.idea_text_renderer.destroy()
    this.viewport_renderer.destroy()
  }
}
